import { useTranslation } from 'react-i18next';

import { useParcelHistory } from '../../../hooks/useParcelHistory';
import type { PaymentHistoryMode } from '../../../types/paymentHistory';
import { PaymentHistoryDrawer } from '../PaymentHistoryDrawer';

export interface ParcelHistoryDrawerProps {
  /** `null` mantém o drawer fechado. */
  readonly parcelId: number | null;
  readonly mode: PaymentHistoryMode | null;
  /** Número da parcela, exibido no subtítulo. */
  readonly parcelNumber: number | null | undefined;
  readonly onClose: () => void;
}

/**
 * Painel lateral com o histórico de uma parcela.
 *
 * Serve os dois modos de leitura do menu de operações — rastreabilidade
 * (`GET /parcels/{parcelId}/traceability`, alterações campo a campo) e
 * acompanhamento do pedido (`GET /parcels/{parcelId}/order-tracking`, eventos de
 * integração).
 *
 * A apresentação vive em `PaymentHistoryDrawer`, compartilhada com o histórico do
 * plano financeiro: aqui ficam apenas o escopo (o hook da parcela) e os textos.
 */
export function ParcelHistoryDrawer({
  parcelId,
  mode,
  parcelNumber,
  onClose,
}: ParcelHistoryDrawerProps) {
  const { t } = useTranslation('individualPayment');
  const history = useParcelHistory(parcelId, mode);

  return (
    <PaymentHistoryDrawer
      open={parcelId !== null && mode !== null}
      mode={mode}
      history={history}
      title={
        mode === 'orderTracking'
          ? t('parcels.history.orderTrackingTitle')
          : t('parcels.history.traceabilityTitle')
      }
      subtitle={
        parcelNumber != null ? t('parcels.history.subtitle', { number: parcelNumber }) : undefined
      }
      emptyHint={
        mode === 'orderTracking'
          ? t('parcels.history.emptyOrderTrackingHint')
          : t('parcels.history.emptyTraceabilityHint')
      }
      onClose={onClose}
    />
  );
}
